import React, { FC } from 'react';
import { useLocale } from '@app/core/hooks/useLocale';
import styles from '../../opm-summary-waterfall-erf.module.scss';
import {
  OpmSampleWaterfallsDto,
  OpmSampleWaterfallInstrumentDto,
  OpmSampleWaterfallInstrumentTranchesDto,
  OpmWaterfallConditionDto,
  OpmWaterfallConditionMomHurdleDetailsDto,
} from '@app/shared/models/contracts/opm-calculation-results-dto';
import classNames from 'classnames';
import {
  CalcMethod,
  IRRHurdleCalculationsNavigatedSourcePage,
  TargetMetric,
  TrancheKey,
} from '@app/shared/models/contracts/enums/shared-enums';
import useIsErfProject from '@app/shared/hooks/useIsErfProject';
import SvgChevron from '@app/shared/icons/Chevron';
import { ExpandableKeys, ExpandedSectionsState } from './SampleWaterfallOutputsSummary';
import { getProceedsCapToolTipContent } from '@app/shared/helpers/proceeds-cap/proceeds-cap';
import {
  formatValueForTargetMetric,
  getMetric,
  getOperator,
  noEscapeValueInterpolationOption,
} from '@app/shared/helpers/localisation/localisation-options';
import OrdinaryEquityRow from './OrdinaryEquityRow';
import ToeOeConditionsRows from './ToeOeConditionsRows';
import WaterfallValueCell from './WaterfallValueCell';
import { useAppSelector } from '@app/core/hooks/redux-hooks';
import { enumKeyByValue } from '@app/shared/helpers';
import { useIrrHurdleNavigation } from '@app/shared/hooks/useIrrHurdleNavigation';
import {
  getConditionsAcrossScenarios,
  getOrdinaryEquityConditionsAcrossScenarios,
  getTrancheForColumn,
  getTrancheKeysAcrossScenarios,
  NOT_APPLICABLE,
  WaterfallCellValue,
} from './sample-waterfall-column-helpers';

type TrancheValueKeys = Omit<
  OpmSampleWaterfallInstrumentTranchesDto,
  'conditions' | 'ordinaryEquityMetrics'
>;
type InstrumentValueKeys = Omit<OpmSampleWaterfallInstrumentDto, 'tranches'>;
type BaseValueType = WaterfallCellValue;
type OrdinaryEquityInstrument = OpmSampleWaterfallInstrumentDto & { instrumentId: string };
type TrancheMetricRow = Pick<
  React.ComponentProps<typeof OrdinaryEquityRow>,
  'label' | 'percentage' | 'upper' | 'canHavePlusSign' | 'canHaveTopLine' | 'hideRowIfEmpty'
> & {
  valueKey: keyof TrancheValueKeys;
  showTooltip?: boolean;
};

interface OrdinaryEquitySectionProps {
  waterfallResults: Dictionary<OpmSampleWaterfallsDto>;
  columnKeys: string[];
  ordinaryEquityInstruments: OrdinaryEquityInstrument[];
  onToggle: (key: string, subKey?: string) => void;
  expandedSections: ExpandedSectionsState;
  exitDatesByColumn: Dictionary<string>;
}

const OrdinaryEquitySection: FC<OrdinaryEquitySectionProps> = ({
  waterfallResults,
  columnKeys,
  ordinaryEquityInstruments,
  onToggle,
  expandedSections,
  exitDatesByColumn,
}): JSX.Element => {
  const { l } = useLocale();
  const isErf = useIsErfProject();

  const projectUsesOpm = useAppSelector(
    (state) =>
      state.project.projectDraft.details.calcMethod === CalcMethod.OPM ||
      state.project.projectDraft.details.calcMethod ===
        enumKeyByValue(CalcMethod, CalcMethod.PWERM_AND_OPM)
  );

  const handleClick = useIrrHurdleNavigation({
    selectedYear: exitDatesByColumn[columnKeys[0]] ?? '',
    sourcePage: IRRHurdleCalculationsNavigatedSourcePage.SampleWaterfall,
  });

  const toolTipContent = (
    instrumentId: string,
    valueKey: keyof TrancheValueKeys,
    trancheKey: string
  ): string | null =>
    columnKeys.reduce<string | null>((content, colKey) => {
      const tranche = getTrancheForColumn(waterfallResults, colKey, instrumentId, trancheKey);

      if (!tranche || tranche[valueKey] === null) {
        return content;
      }

      return getProceedsCapToolTipContent(
        valueKey,
        [tranche.hasHitCap],
        [tranche.hasReceivedEnhancedParticipation]
      );
    }, null);

  const hasValue = (value: BaseValueType): boolean => value !== NOT_APPLICABLE && Boolean(value);

  const hurdleHasCumulativeChange = (
    instrumentId: string,
    trancheKey: string,
    conditionIndex: number
  ) => {
    return columnKeys.some((colKey) => {
      const cashPaidValue = getMomHurdleValue(
        instrumentId,
        trancheKey,
        conditionIndex,
        'cumulativeCashPaid'
      )(colKey);
      const dividendsValue = getMomHurdleValue(
        instrumentId,
        trancheKey,
        conditionIndex,
        'cumulativeDividends'
      )(colKey);
      const redemptionsValue = getMomHurdleValue(
        instrumentId,
        trancheKey,
        conditionIndex,
        'cumulativeRedemptions'
      )(colKey);
      return hasValue(cashPaidValue) || hasValue(dividendsValue) || hasValue(redemptionsValue);
    });
  };

  const shouldRenderCumulativeInfusionRow = (
    instrumentId: string,
    trancheKey: string,
    conditionIndex: number
  ) => {
    return columnKeys.some((colKey) => {
      const condition = getTrancheForColumn(waterfallResults, colKey, instrumentId, trancheKey)
        ?.conditions?.[conditionIndex];

      return (
        condition?.metric === enumKeyByValue(TargetMetric, TargetMetric.InstitutionalMoM) &&
        hasValue(
          getConditionHurdleValue(
            instrumentId,
            trancheKey,
            conditionIndex,
            'cumulativeInfusions'
          )(colKey)
        )
      );
    });
  };

  const getTrancheValue = (
    instrumentId: string,
    trancheKey: string,
    valueKey: keyof TrancheValueKeys
  ): ((columnKey: string) => BaseValueType) => {
    return (colKey: string) => {
      const tranche = getTrancheForColumn(waterfallResults, colKey, instrumentId, trancheKey);

      if (!tranche) {
        return NOT_APPLICABLE;
      }

      if (tranche[valueKey] === null) {
        return null;
      }

      const hasMultipleConditions = tranche.conditions.length > 1;

      if (valueKey === 'activeHurdle' && !hasMultipleConditions) {
        return null;
      }

      return tranche[valueKey];
    };
  };

  const getMomHurdleValue = (
    instrumentId: string,
    trancheKey: string,
    conditionIndex: number,
    valueKey: keyof OpmWaterfallConditionMomHurdleDetailsDto
  ): ((columnKey: string) => BaseValueType) => {
    return (colKey: string) => {
      const condition = getTrancheForColumn(waterfallResults, colKey, instrumentId, trancheKey)
        ?.conditions?.[conditionIndex];

      if (!condition) {
        return NOT_APPLICABLE;
      }

      return condition.momHurdleDetails?.[valueKey];
    };
  };

  const getConditionHurdleValue = (
    instrumentId: string,
    trancheKey: string,
    conditionIndex: number,
    valueKey: keyof OpmWaterfallConditionDto
  ): ((columnKey: string) => BaseValueType) => {
    return (colKey: string) => {
      const condition = getTrancheForColumn(waterfallResults, colKey, instrumentId, trancheKey)
        ?.conditions?.[conditionIndex];

      if (!condition) {
        return NOT_APPLICABLE;
      }

      const value = condition[valueKey];
      // Ensure we only return primitive values
      if (typeof value === 'object' && value !== null) {
        return undefined;
      }
      return value as BaseValueType;
    };
  };

  const getIrrTestValue = (
    instrumentId: string,
    trancheKey: string,
    conditionIndex: number
  ): ((columnKey: string) => BaseValueType) => {
    return (colKey: string) => {
      const condition = getTrancheForColumn(waterfallResults, colKey, instrumentId, trancheKey)
        ?.conditions?.[conditionIndex];

      return condition ? condition.equivalentHurdleValue : NOT_APPLICABLE;
    };
  };

  const getInstrumentValue = (
    instrumentId: string,
    valueKey: keyof InstrumentValueKeys
  ): ((colKey: string) => BaseValueType) => {
    return (colKey: string) => {
      const instrument = waterfallResults[colKey]?.instruments?.[instrumentId];
      return instrument ? instrument[valueKey] : NOT_APPLICABLE;
    };
  };

  const oeHasBuybacks = (instrumentId: string, trancheKey: string): boolean =>
    columnKeys.some(
      (colKey) =>
        getTrancheForColumn(
          waterfallResults,
          colKey,
          instrumentId,
          trancheKey
        )?.ordinaryEquityMetrics?.conditions.some(
          (condition) => (condition.toeHurdleDetails?.cumulativeRedemptions ?? 0) !== 0
        ) ?? false
    );

  const oeHasDividends = (instrumentId: string, trancheKey: string): boolean =>
    columnKeys.some(
      (colKey) =>
        getTrancheForColumn(
          waterfallResults,
          colKey,
          instrumentId,
          trancheKey
        )?.ordinaryEquityMetrics?.conditions.some(
          (condition) => (condition.toeHurdleDetails?.cumulativeDividends ?? 0) !== 0
        ) ?? false
    );

  const renderExpandToggle = (
    expanded: boolean | undefined,
    key: ExpandableKeys,
    subKey?: string,
    level?: 'instrument' | 'tranche'
  ) => (
    <div
      className={classNames(styles['expand-toggle'], level && styles[`expand-toggle--${level}`])}>
      <SvgChevron
        onClick={() => onToggle(key, subKey)}
        className={styles[expanded ? 'chevron-expanded' : 'chevron-collapsed']}
      />
    </div>
  );

  const renderInstrument = (instrument: OrdinaryEquityInstrument) => {
    const trancheKeys = getTrancheKeysAcrossScenarios(
      waterfallResults,
      columnKeys,
      instrument.instrumentId
    );

    return (
      <React.Fragment key={instrument.instrumentId}>
        <tr key={instrument.instrumentId}>
          <th
            className={classNames(styles['section-header'], styles['section-header--indented'])}
            colSpan={columnKeys.length + 1}>
            {renderExpandToggle(
              expandedSections.ordinaryEquityInstruments?.[instrument.instrumentId],
              ExpandableKeys.OrdinaryEquityInstruments,
              instrument.instrumentId,
              'instrument'
            )}
            {instrument.instrumentName}
          </th>
        </tr>
        {expandedSections.ordinaryEquityInstruments?.[instrument.instrumentId] &&
          trancheKeys.map((trancheKey, trancheIndex) =>
            renderTranche(instrument, trancheKeys, trancheKey, trancheIndex)
          )}
      </React.Fragment>
    );
  };

  const renderTranche = (
    instrument: OrdinaryEquityInstrument,
    trancheKeys: string[],
    trancheKey: string,
    trancheIndex: number
  ) => {
    const hasMultipleTranches = trancheKeys.length > 1;
    const hasOneTranche =
      !trancheKeys.includes(TrancheKey.DefaultTrancheKey) && trancheKeys.length === 1;
    const hasNoTranches = trancheKeys.includes(TrancheKey.DefaultTrancheKey);
    const isLastTranche = trancheIndex + 1 === trancheKeys.length;

    return (
      <React.Fragment key={`tranche-${trancheKey}-${instrument.instrumentId}`}>
        {!hasNoTranches && !hasOneTranche && (
          <tr key={`tranche-subheader-${trancheIndex}`}>
            <th className={styles['sub-section-header']} colSpan={columnKeys.length + 1}>
              {renderExpandToggle(
                expandedSections.ordinaryEquityTranches?.[trancheKey],
                ExpandableKeys.OrdinaryEquityTranches,
                trancheKey,
                'tranche'
              )}
              {`Tranche ${trancheIndex + 1}`}
            </th>
          </tr>
        )}
        {(hasNoTranches ||
          hasOneTranche ||
          expandedSections.ordinaryEquityTranches?.[trancheKey]) && (
          <>
            <OrdinaryEquityRow
              columnKeys={columnKeys}
              label={l(isErf ? '_InitialSponsorCost' : '_SponsorCost')}
              valueSelector={getTrancheValue(
                instrument.instrumentId,
                trancheKey,
                'institutionalInvestment'
              )}
              rowTestId={`institutionalInvestment-tranche${trancheIndex + 1}-title`}
              trancheIndentation={hasMultipleTranches}
              hideRowIfEmpty
            />
            {renderConditions(instrument, trancheKey, hasMultipleTranches)}
            {renderTrancheMetrics(
              instrument.instrumentId,
              trancheKey,
              trancheIndex,
              hasMultipleTranches,
              hasNoTranches
            )}
            {isLastTranche &&
              renderInstrumentSummary(instrument, hasOneTranche, hasMultipleTranches)}
          </>
        )}
      </React.Fragment>
    );
  };

  const renderConditions = (
    instrument: OrdinaryEquityInstrument,
    trancheKey: string,
    hasMultipleTranches: boolean
  ) => {
    const oeConditions = getOrdinaryEquityConditionsAcrossScenarios(
      waterfallResults,
      columnKeys,
      instrument.instrumentId,
      trancheKey
    );
    const hasToeOeConditions = oeConditions.length > 0;
    const hasBuybacks = oeHasBuybacks(instrument.instrumentId, trancheKey);
    const hasDividends = oeHasDividends(instrument.instrumentId, trancheKey);
    return (
      <>
        {getConditionsAcrossScenarios(
          waterfallResults,
          columnKeys,
          instrument.instrumentId,
          trancheKey
        ).map((condition, index) =>
          renderSponsorCondition(instrument, trancheKey, hasMultipleTranches, condition, index)
        )}
        {hasToeOeConditions && (
          <ToeOeConditionsRows
            oeConditions={oeConditions}
            hasBuybacks={hasBuybacks}
            hasDividends={hasDividends}
            columnKeys={columnKeys}
            hasMultipleTranches={hasMultipleTranches}
            instrumentId={instrument.instrumentId}
            trancheKey={trancheKey}
            waterfallResults={waterfallResults}
          />
        )}
      </>
    );
  };

  const renderSponsorCondition = (
    instrument: OrdinaryEquityInstrument,
    trancheKey: string,
    hasMultipleTranches: boolean,
    condition: OpmWaterfallConditionDto,
    index: number
  ) => {
    const sponsorHurdleLabel = `${l('_SponsorConditionPlaceholders', {
      metric: getMetric(condition.metric),
      operator: getOperator(condition.operator),
      value: formatValueForTargetMetric(condition.metric, condition.testValue),
      interpolation: noEscapeValueInterpolationOption,
    })}`;

    const conditionReference = condition.metric + ' - ' + condition.testValue;

    const isIrrCondition =
      condition.metric === enumKeyByValue(TargetMetric, TargetMetric.InstitutionalIRR);

    const conditionValueSelector = isIrrCondition
      ? getIrrTestValue(instrument.instrumentId, trancheKey, index)
      : getMomHurdleValue(instrument.instrumentId, trancheKey, index, 'rawSponsorHurdleMom');

    return (
      <React.Fragment key={`condition-${index}`}>
        {Boolean(condition.cumulativeInfusions) &&
          shouldRenderCumulativeInfusionRow(instrument.instrumentId, trancheKey, index) && (
            <>
              <OrdinaryEquityRow
                columnKeys={columnKeys}
                label={l('_AddCumulativeInfusion')}
                valueSelector={getConditionHurdleValue(
                  instrument.instrumentId,
                  trancheKey,
                  index,
                  'cumulativeInfusions'
                )}
                rowTestId={`cumulativeInfusions-${index + 1}-title`}
                trancheIndentation={hasMultipleTranches}
                hideRowIfEmpty
              />
              <OrdinaryEquityRow
                columnKeys={columnKeys}
                label={l('_TotalSponsorCost')}
                valueSelector={getConditionHurdleValue(
                  instrument.instrumentId,
                  trancheKey,
                  index,
                  'totalSponsorCost'
                )}
                rowTestId={`totalSponsorCost-${index + 1}-title`}
                trancheIndentation={hasMultipleTranches}
                hideRowIfEmpty
              />
            </>
          )}
        <OrdinaryEquityRow
          columnKeys={columnKeys}
          label={sponsorHurdleLabel}
          valueSelector={conditionValueSelector}
          cellClickFunction={
            projectUsesOpm && isIrrCondition
              ? (columnKey) =>
                  handleClick(
                    instrument.instrumentId,
                    conditionReference,
                    sponsorHurdleLabel,
                    exitDatesByColumn[columnKey]
                  )
              : undefined
          }
          rowTestId={`rawSponsorHurdleMom-${index + 1}-title`}
          trancheIndentation={hasMultipleTranches}
        />
        {condition.momHurdleDetails !== null && (
          <>
            {renderMomAdjustments(instrument.instrumentId, trancheKey, index, hasMultipleTranches)}
            {hurdleHasCumulativeChange(instrument.instrumentId, trancheKey, index) && (
              <OrdinaryEquityRow
                columnKeys={columnKeys}
                label={l('_NetSponsorConditionPlaceholders', {
                  metric: getMetric(condition.metric),
                  operator: getOperator(condition.operator),
                  value: formatValueForTargetMetric(condition.metric, condition.testValue),
                  interpolation: noEscapeValueInterpolationOption,
                })}
                valueSelector={getMomHurdleValue(
                  instrument.instrumentId,
                  trancheKey,
                  index,
                  'conditionalValue'
                )}
                rowTestId={`conditionalValue-${index + 1}-title`}
                trancheIndentation={hasMultipleTranches}
              />
            )}
          </>
        )}
      </React.Fragment>
    );
  };

  const renderMomAdjustments = (
    instrumentId: string,
    trancheKey: string,
    conditionIndex: number,
    hasMultipleTranches: boolean
  ) => {
    const rows: { valueKey: keyof OpmWaterfallConditionMomHurdleDetailsDto; label: string }[] = [
      { valueKey: 'cumulativeRedemptions', label: l('_LessCumulativeRedemption') },
      { valueKey: 'cumulativeCashPaid', label: l('_LessCumulativeCashInterest') },
      { valueKey: 'cumulativeDividends', label: l('_LessCumulativeDividends') },
    ];

    return rows.map(({ valueKey, label }) => (
      <OrdinaryEquityRow
        key={valueKey}
        columnKeys={columnKeys}
        label={label}
        valueSelector={getMomHurdleValue(instrumentId, trancheKey, conditionIndex, valueKey)}
        rowTestId={`${valueKey}-${conditionIndex + 1}-title`}
        trancheIndentation={hasMultipleTranches}
        hideRowIfEmpty
      />
    ));
  };

  const renderTrancheMetrics = (
    instrumentId: string,
    trancheKey: string,
    trancheIndex: number,
    hasMultipleTranches: boolean,
    hasNoTranches: boolean
  ) => {
    const rows: TrancheMetricRow[] = [
      { valueKey: 'activeHurdle', label: l('_InstitutionHurdleActive') },
      { valueKey: 'lessSponsorDebt', label: l('_LessSponsorDebtColon') },
      { valueKey: 'hurdleRemaining', label: l('_HurdleRemaining') },
      { valueKey: 'grossUp', label: l('_GrossUpPercent'), percentage: true },
      { valueKey: 'ordinaryEquityHurdle', label: l('_EffectiveHurdle') },
      { valueKey: 'isHurdleMet', label: l('_IsHurdleMet'), upper: true },
      { valueKey: 'ordinaryEquityPostHurdle', label: l('_OrdinaryEquityPostHurdle') },
      {
        valueKey: 'trancheParticipationPc',
        label: l(hasNoTranches ? '_PcShareholding' : '_PcParticipation'),
        percentage: true,
        canHavePlusSign: true,
        showTooltip: true,
      },
      { valueKey: 'proceedsCap', label: l('_MaximumPayoutCap') },
      {
        valueKey: 'trancheDistribution',
        label: l('_DistributionsPaid'),
        canHaveTopLine: true,
        hideRowIfEmpty: false,
        showTooltip: true,
      },
    ];

    return rows.map(({ valueKey, showTooltip, hideRowIfEmpty = true, ...rowProps }) => (
      <OrdinaryEquityRow
        key={valueKey}
        {...rowProps}
        columnKeys={columnKeys}
        valueSelector={getTrancheValue(instrumentId, trancheKey, valueKey)}
        rowTestId={`${valueKey}-tranche${trancheIndex + 1}-title`}
        trancheIndentation={hasMultipleTranches}
        hideRowIfEmpty={hideRowIfEmpty}
        toolTipContent={
          showTooltip ? toolTipContent(instrumentId, valueKey, trancheKey) : undefined
        }
      />
    ));
  };

  const renderInstrumentSummary = (
    instrument: OrdinaryEquityInstrument,
    hasOneTranche: boolean,
    hasMultipleTranches: boolean
  ) => {
    // ! needs to be updated to also consider net proceeds to instrument
    const hasMovement = columnKeys.some((colKey) =>
      Boolean(waterfallResults[colKey]?.instruments?.[instrument.instrumentId]?.cumulativeMovement)
    );
    const hasDividend = columnKeys.some((colKey) =>
      Boolean(waterfallResults[colKey]?.instruments?.[instrument.instrumentId]?.cumulativeDividends)
    );

    return (
      <>
        {/* Below needs to change, and use a property that has not yet been implemented for the net distribution at the instrument level */}
        {!hasOneTranche &&
          columnKeys.some(
            (colKey) =>
              waterfallResults[colKey]?.instruments?.[instrument.instrumentId]?.proceedsCap
          ) && (
            <OrdinaryEquityRow
              columnKeys={columnKeys}
              label={l('_MaximumPayoutCap')}
              valueSelector={getInstrumentValue(instrument.instrumentId, 'proceedsCap')}
              rowTestId="proceedsCap-title"
            />
          )}
        {!hasOneTranche && hasMultipleTranches && (
          <OrdinaryEquityRow
            columnKeys={columnKeys}
            label={l('_TotalProceedsToInstrument', {
              instrument: instrument.instrumentName,
              interpolation: noEscapeValueInterpolationOption,
            })}
            valueSelector={getInstrumentValue(instrument.instrumentId, 'totalDistributions')}
            rowTestId="totalDistributions-title"
          />
        )}
        {(hasMovement || hasDividend) && (
          <>
            <tr>
              <td
                className={classNames(styles['border-right'], [
                  styles['instrument-indentation'],
                  styles['bold-text'],
                ])}>
                {l('_CumulativeCashFlowMovement')}
              </td>
              {columnKeys.map((key) => (
                <td key={key} />
              ))}
            </tr>
            <OrdinaryEquityRow
              columnKeys={columnKeys}
              label={l('_NetInfusionOrBuybackNegative')}
              valueSelector={getInstrumentValue(instrument.instrumentId, 'cumulativeMovement')}
              rowTestId="cumulativeMovement-title"
            />
            <OrdinaryEquityRow
              columnKeys={columnKeys}
              label={l('_Dividends')}
              valueSelector={getInstrumentValue(instrument.instrumentId, 'cumulativeDividends')}
              rowTestId="cumulativeMovement-title"
              hideRowIfEmpty
            />
          </>
        )}
      </>
    );
  };

  return (
    <>
      <tr>
        <th className={styles['section-header']} colSpan={columnKeys.length + 1}>
          {renderExpandToggle(expandedSections.ordinaryEquity, ExpandableKeys.OrdinaryEquity)}
          {l('_OrdinaryEquityTitle')}
        </th>
      </tr>
      {expandedSections.ordinaryEquity && (
        <>
          <tr>
            <td>{l('_OrdinaryEquityTitle')}</td>
            {columnKeys.map((key) => {
              const result = waterfallResults[key];
              return (
                <WaterfallValueCell
                  key={key}
                  value={result ? result.totalOrdinaryEquityProceeds : NOT_APPLICABLE}
                />
              );
            })}
          </tr>
          {ordinaryEquityInstruments.map(renderInstrument)}
        </>
      )}
    </>
  );
};

export default OrdinaryEquitySection;
